import { useState, useEffect, useCallback, Component, type ReactNode, type ErrorInfo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FlaskConical,
  ArrowLeft,
  ListChecks,
  Check,
  LogOut,
  Loader2,
  AlertCircle,
  HelpCircle,
  ClipboardList,
} from 'lucide-react';
import { useApp, useExperiment, useProgress } from '@/context';
import { useLab } from '@/context/LabContext';
import { getExperimentConfig as getChem2131Config } from '@/config/experiments/chem2131';
import { getExperimentConfig as getChem1131Config } from '@/config/experiments/chem1131';
import { COURSES } from '@/config/courses.config';
import type { ExperimentConfig, ExperimentPart, ExperimentStep } from '@/types';
import LabBench from '@/components/experiments/LabBench';
import AIChatPanel from '@/components/ai/AIChatPanel';
import InfoBox from '@/components/experiments/InfoBox';
import { QuizPanel } from '@/components/quiz';

// Dynamic experiment renderers - these will be extended as more experiments are built
import Part1Purification from '@/components/experiments/Part1Purification';
import Part2Analysis from '@/components/experiments/Part2Analysis';

// Experiment 3: Phosphate in Cola
import {
  Part1SamplePrep,
  Part2Standards,
  Part3Measurement,
  Part4Analysis,
} from '@/components/experiments/exp3-phosphate';

// Experiment 8: Nitrite Content Determination
import {
  Part1Preparation as Exp8Part1Preparation,
  Part2Measurement as Exp8Part2Measurement,
} from '@/components/experiments/exp8-nitrite';

// Experiment 9: Iron Determination in Soil
import {
  Part1Extraction,
  Part2ColorDevelopment,
  Part3Measurement as Exp9Part3Measurement,
} from '@/components/experiments/exp9-iron';

// Experiment 1: Emission Spectra
import {
  Part1Setup as Exp1Part1Setup,
  Part2Measurement as Exp1Part2Measurement,
  Part3Calculation as Exp1Part3Calculation,
  Part4Comparison as Exp1Part4Comparison,
} from '@/components/experiments/exp1-emission';

// Experiment 5: Soaps and Detergents
import {
  Part1SoapMaking,
  Part2DetergentMaking,
  Part3PropertyTesting,
} from '@/components/experiments/exp5-soaps';

// Experiment 7: Gravimetric Analysis
import {
  Part1Chloride,
  Part2Phosphorus,
} from '@/components/experiments/exp7-gravimetric';

// Experiment 10: Chemical Equilibrium
import {
  Part1LeChatelier,
  Part2KaDetermination,
} from '@/components/experiments/exp10-equilibrium';

// Experiment 4: EDTA Standardization
import {
  Part1Preparation as Exp4Part1Preparation,
  Part2Standardization as Exp4Part2Standardization,
  Part3SampleAnalysis as Exp4Part3SampleAnalysis,
  Part4Calculations as Exp4Part4Calculations,
} from '@/components/experiments/exp4-edta';

// Experiment 6: Vitamin C Analysis
import {
  Part1SamplePrep as Exp6Part1SamplePrep,
  Part2Titration as Exp6Part2Titration,
  Part3Calculations as Exp6Part3Calculations,
} from '@/components/experiments/exp6-vitaminc';

// Experiment 11: Aspirin Determination
import {
  Part1Calibration as Exp11Part1Calibration,
  Part2SamplePrep as Exp11Part2SamplePrep,
  Part3Titration as Exp11Part3Titration,
  Part4Analysis as Exp11Part4Analysis,
} from '@/components/experiments/exp11-aspirin';

// CHEM2131 Summary Components
import Exp1Summary from '@/components/experiments/shared/summaries/Exp1Summary';
import Exp2Summary from '@/components/experiments/shared/summaries/Exp2Summary';
import Exp3Summary from '@/components/experiments/shared/summaries/Exp3Summary';
import Exp4Summary from '@/components/experiments/shared/summaries/Exp4Summary';
import Exp5Summary from '@/components/experiments/shared/summaries/Exp5Summary';
import Exp6Summary from '@/components/experiments/shared/summaries/Exp6Summary';
import Exp7Summary from '@/components/experiments/shared/summaries/Exp7Summary';
import Exp8Summary from '@/components/experiments/shared/summaries/Exp8Summary';
import Exp9Summary from '@/components/experiments/shared/summaries/Exp9Summary';
import Exp10Summary from '@/components/experiments/shared/summaries/Exp10Summary';
import Exp11Summary from '@/components/experiments/shared/summaries/Exp11Summary';

// ============================================================================
// CHEM1131 Experiment Imports
// ============================================================================

// CHEM1131 Experiment 4: Molar Mass of Compounds
import {
  Part1Introduction as Chem1131Exp4Part1,
} from '@/components/experiments/chem1131/exp4-molarmass';

// CHEM1131 Experiment 8: Aluminum Compound
import {
  Part1Dissolution as Chem1131Exp8Part1,
} from '@/components/experiments/chem1131/exp8-aluminum';

// CHEM1131 Experiment 9: Acetic Acid Titration
import {
  Part1VinegarPrep as Chem1131Exp9Part1,
  Part2BuretteSetup as Chem1131Exp9Part2,
  Part3Titration as Chem1131Exp9Part3,
} from '@/components/experiments/chem1131/exp9-aceticacid';

// CHEM1131 Experiment 11: Empirical Formula (Tin Oxide)
import {
  Part1Preparation as Chem1131Exp11Part1,
  Part2InitialHeating as Chem1131Exp11Part2,
  Part3TinPreparation as Chem1131Exp11Part3,
  Part4AcidAddition as Chem1131Exp11Part4,
  Part5Evaporation as Chem1131Exp11Part5,
  Part6StrongHeating as Chem1131Exp11Part6,
  Part7FinalWeighing as Chem1131Exp11Part7,
  Part8Calculations as Chem1131Exp11Part8,
} from '@/components/experiments/chem1131/exp11-empirical';

// CHEM1131 Experiment 1: Household Compounds
import {
  Part1Setup as Chem1131Exp1Part1,
  Part2Solubility as Chem1131Exp1Part2,
  Part3Iodine as Chem1131Exp1Part3,
  Part4Vinegar as Chem1131Exp1Part4,
  Part5Phenolphthalein as Chem1131Exp1Part5,
  Part6Differentiation as Chem1131Exp1Part6,
  Part7Unknown as Chem1131Exp1Part7,
} from '@/components/experiments/chem1131/exp1-household';

// CHEM1131 Experiment 2: Cation Analysis
import {
  Part1Setup as Chem1131Exp2Part1,
  Part2CopperTest as Chem1131Exp2Part2,
  Part3SilverTest as Chem1131Exp2Part3,
  Part4NickelTest as Chem1131Exp2Part4,
  Part5AluminumTest as Chem1131Exp2Part5,
  Part6Unknown as Chem1131Exp2Part6,
} from '@/components/experiments/chem1131/exp2-cations';

// CHEM1131 Experiment 5: Copper Cycle
import {
  Part1Dissolution as Chem1131Exp5Part1,
  Part2Precipitation as Chem1131Exp5Part2,
  Part3Dehydration as Chem1131Exp5Part3,
  Part4Dissolution2 as Chem1131Exp5Part4,
  Part5Sulfate as Chem1131Exp5Part5,
  Part6Reduction as Chem1131Exp5Part6,
  Part7Washing as Chem1131Exp5Part7,
  Part8Drying as Chem1131Exp5Part8,
  Part9Weighing as Chem1131Exp5Part9,
  Part10Yield as Chem1131Exp5Part10,
} from '@/components/experiments/chem1131/exp5-coppercycle';

// CHEM1131 Experiment 3: Anion Analysis
import {
  Part1Setup as Chem1131Exp3Part1,
  Part2Carbonate as Chem1131Exp3Part2,
  Part3Halide as Chem1131Exp3Part3,
  Part4Iodide as Chem1131Exp3Part4,
  Part5Sulfate as Chem1131Exp3Part5,
} from '@/components/experiments/chem1131/exp3-anions';

// CHEM1131 Experiment 6: Salt Formation
import {
  directCombination as Chem1131Exp6Part1,
  neutralization as Chem1131Exp6Part2,
  acidOxide as Chem1131Exp6Part3,
  acidCarbonate as Chem1131Exp6Part4,
  acidMetal as Chem1131Exp6Part5,
  doubleDisplacement as Chem1131Exp6Part6,
} from '@/components/experiments/chem1131/exp6-saltformation';

// CHEM1131 Experiment 7: Salt Solubility
import {
  Part1Setup as Chem1131Exp7Part1,
  Part2Chloride as Chem1131Exp7Part2,
  Part3Bromide as Chem1131Exp7Part3,
  Part4Iodide as Chem1131Exp7Part4,
  Part5Sulfide as Chem1131Exp7Part5,
  Part6Sulfate as Chem1131Exp7Part6,
  Part7Carbonate as Chem1131Exp7Part7,
  Part8PhosphateHydroxide as Chem1131Exp7Part8,
  Part9Analysis as Chem1131Exp7Part9,
} from '@/components/experiments/chem1131/exp7-solubility';

// CHEM1131 Experiment 10: Antacid Analysis
import {
  introduction as Chem1131Exp10Part1,
  sample_preparation as Chem1131Exp10Part2,
  burette_setup as Chem1131Exp10Part3,
  titration as Chem1131Exp10Part4,
  repeat_trials as Chem1131Exp10Part5,
  calculations as Chem1131Exp10Part6,
} from '@/components/experiments/chem1131/exp10-antacid';

// Error boundary to catch runtime crashes in experiment components
class ExperimentErrorBoundary extends Component<
  { children: ReactNode; onReset?: () => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode; onReset?: () => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Experiment render error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: 'center', color: '#ccc' }}>
          <AlertCircle size={48} style={{ color: '#EF4444', marginBottom: 16 }} />
          <h3 style={{ color: '#fff', marginBottom: 8 }}>Something went wrong</h3>
          <p style={{ fontSize: 13, marginBottom: 16 }}>{this.state.error?.message}</p>
          <button
            onClick={() => {
              // Clear stale experiment state from localStorage
              Object.keys(localStorage).forEach(k => {
                if (k.startsWith('virtuallab_exp_')) localStorage.removeItem(k);
              });
              this.setState({ hasError: false, error: null });
              this.props.onReset?.();
              window.location.reload();
            }}
            style={{
              padding: '10px 24px', background: '#FF5A36', color: '#fff',
              border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600
            }}
          >
            Reset & Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

interface ExperimentRenderers {
  [courseId: string]: {
    [expId: string]: {
      [partKey: string]: React.ComponentType;
    };
  };
}

// Map course and experiment IDs to their custom renderers
// CHEM1131 experiments use the generic renderer (no custom visualizations yet)
const experimentRenderers: ExperimentRenderers = {
  chem2131: {
    exp1: {
      summary: Exp1Summary,
      setup: Exp1Part1Setup,
      measurement: Exp1Part2Measurement,
      calculation: Exp1Part3Calculation,
      comparison: Exp1Part4Comparison,
    },
    exp2: {
      summary: Exp2Summary,
      part1: Part1Purification,
      part2: Part2Analysis,
    },
    exp3: {
      summary: Exp3Summary,
      samplePrep: Part1SamplePrep,
      standards: Part2Standards,
      measurement: Part3Measurement,
      analysis: Part4Analysis,
    },
    exp4: {
      summary: Exp4Summary,
      preparation: Exp4Part1Preparation,
      standardization: Exp4Part2Standardization,
      sampleAnalysis: Exp4Part3SampleAnalysis,
      calculations: Exp4Part4Calculations,
    },
    exp5: {
      summary: Exp5Summary,
      soapMaking: Part1SoapMaking,
      detergentMaking: Part2DetergentMaking,
      propertyTesting: Part3PropertyTesting,
    },
    exp6: {
      summary: Exp6Summary,
      samplePrep: Exp6Part1SamplePrep,
      titration: Exp6Part2Titration,
      calculations: Exp6Part3Calculations,
    },
    exp7: {
      summary: Exp7Summary,
      chloride: Part1Chloride,
      phosphorus: Part2Phosphorus,
    },
    exp8: {
      summary: Exp8Summary,
      preparation: Exp8Part1Preparation,
      measurement: Exp8Part2Measurement,
    },
    exp9: {
      summary: Exp9Summary,
      extraction: Part1Extraction,
      colorDevelopment: Part2ColorDevelopment,
      measurement: Exp9Part3Measurement,
    },
    exp10: {
      summary: Exp10Summary,
      leChatelier: Part1LeChatelier,
      kaDetermination: Part2KaDetermination,
    },
    exp11: {
      summary: Exp11Summary,
      calibration: Exp11Part1Calibration,
      samplePreparation: Exp11Part2SamplePrep,
      titration: Exp11Part3Titration,
      analysis: Exp11Part4Analysis,
    },
  },
  // CHEM1131 experiments with custom visualizations
  chem1131: {
    exp1: {
      setup: Chem1131Exp1Part1,
      solubility: Chem1131Exp1Part2,
      iodine: Chem1131Exp1Part3,
      vinegar: Chem1131Exp1Part4,
      phenolphthalein: Chem1131Exp1Part5,
      differentiation: Chem1131Exp1Part6,
      unknown: Chem1131Exp1Part7,
    },
    exp2: {
      setup: Chem1131Exp2Part1,
      copper: Chem1131Exp2Part2,
      silver: Chem1131Exp2Part3,
      nickel: Chem1131Exp2Part4,
      aluminum: Chem1131Exp2Part5,
      unknown: Chem1131Exp2Part6,
    },
    exp3: {
      setup: Chem1131Exp3Part1,
      carbonate: Chem1131Exp3Part2,
      halide: Chem1131Exp3Part3,
      iodide: Chem1131Exp3Part4,
      sulfate: Chem1131Exp3Part5,
    },
    exp4: {
      selection: Chem1131Exp4Part1,
    },
    exp5: {
      introduction: Chem1131Exp5Part1,
      prepareCopper: Chem1131Exp5Part2,
      reaction1: Chem1131Exp5Part3,
      reaction2: Chem1131Exp5Part4,
      reaction3: Chem1131Exp5Part5,
      wash1: Chem1131Exp5Part6,
      reaction4: Chem1131Exp5Part7,
      reaction5: Chem1131Exp5Part8,
      wash2: Chem1131Exp5Part9,
      calculations: Chem1131Exp5Part10,
    },
    exp6: {
      directCombination: Chem1131Exp6Part1,
      neutralization: Chem1131Exp6Part2,
      acidOxide: Chem1131Exp6Part3,
      acidCarbonate: Chem1131Exp6Part4,
      acidMetal: Chem1131Exp6Part5,
      doubleDisplacement: Chem1131Exp6Part6,
    },
    exp7: {
      setup: Chem1131Exp7Part1,
      chloride: Chem1131Exp7Part2,
      bromide: Chem1131Exp7Part3,
      iodide: Chem1131Exp7Part4,
      sulfide: Chem1131Exp7Part5,
      sulfate: Chem1131Exp7Part6,
      carbonate: Chem1131Exp7Part7,
      phosphateHydroxide: Chem1131Exp7Part8,
      analysis: Chem1131Exp7Part9,
    },
    exp8: {
      dissolution: Chem1131Exp8Part1,
    },
    exp9: {
      vinegar_preparation: Chem1131Exp9Part1,
      burette_setup: Chem1131Exp9Part2,
      vinegar_titration: Chem1131Exp9Part3,
    },
    exp10: {
      introduction: Chem1131Exp10Part1,
      sample_preparation: Chem1131Exp10Part2,
      burette_setup: Chem1131Exp10Part3,
      titration: Chem1131Exp10Part4,
      repeat_trials: Chem1131Exp10Part5,
      calculations: Chem1131Exp10Part6,
    },
    exp11: {
      setup: Chem1131Exp11Part1,
      initial_heating: Chem1131Exp11Part2,
      tin_preparation: Chem1131Exp11Part3,
      acid_addition: Chem1131Exp11Part4,
      evaporation: Chem1131Exp11Part5,
      strong_heating: Chem1131Exp11Part6,
      final_weighing: Chem1131Exp11Part7,
      calculations: Chem1131Exp11Part8,
    },
  },
};

export default function ExperimentPage() {
  const navigate = useNavigate();
  const { courseId, expId } = useParams<{ courseId: string; expId: string }>();
  const { state: appState, logout, setLabMode } = useApp();
  const {
    state: experimentState,
    loadExperiment,
    setCurrentPart,
    setCurrentStep,
    completeStep,
    isStepCompleted,
  } = useExperiment();
  const { markStepComplete, markPartComplete } = useProgress();
  const { update: updateLabState } = useLab();

  const [config, setConfig] = useState<ExperimentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [showQuiz, setShowQuiz] = useState(false);

  const course = courseId ? COURSES[courseId] : null;

  // Load experiment configuration
  useEffect(() => {
    if (!courseId || !expId) {
      setError('Missing course or experiment ID');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Load config based on course ID
    let experimentConfig: ExperimentConfig | undefined;

    if (courseId === 'chem2131') {
      experimentConfig = getChem2131Config(expId);
    } else if (courseId === 'chem1131') {
      experimentConfig = getChem1131Config(expId);
    }

    if (!experimentConfig) {
      setError(`Experiment "${expId}" not found for course "${courseId}"`);
      setLoading(false);
      return;
    }

    setConfig(experimentConfig);

    // Initialize experiment state with initial data from all parts
    const initialData: Record<string, unknown> = {};
    experimentConfig.parts.forEach((part) => {
      initialData[part.name] = { ...part.initialState };
    });

    loadExperiment(courseId, expId, initialData);
    setLoading(false);
  }, [courseId, expId, loadExperiment]);

  // Get current part configuration
  const currentPart: ExperimentPart | undefined = config?.parts.find(
    (p) => p.id === (experimentState?.currentPart ?? 1)
  );

  // Get completed steps count for current part
  const getPartCompletedCount = useCallback(() => {
    if (!experimentState || !currentPart) return 0;
    const partPrefix = `${currentPart.name}-`;
    return experimentState.completedSteps.filter((s) => s.startsWith(partPrefix)).length;
  }, [experimentState, currentPart]);

  // Check if step is active (can be interacted with)
  const isStepActive = useCallback(
    (stepNum: number) => {
      if (appState.labMode === 'free') return true;
      return experimentState?.currentStep === stepNum;
    },
    [appState.labMode, experimentState]
  );

  // Check if step is done
  const isStepDone = useCallback(
    (stepNum: number) => {
      if (!currentPart) return false;
      return isStepCompleted(`${currentPart.name}-${stepNum}`);
    },
    [currentPart, isStepCompleted]
  );

  // Handle step completion
  const handleCompleteStep = useCallback(
    (stepId: string) => {
      if (!courseId || !expId) return;
      completeStep(stepId);
      markStepComplete(courseId, expId, stepId);
    },
    [courseId, expId, completeStep, markStepComplete]
  );

  // Handle part completion
  const handlePartComplete = useCallback(
    (partId: string) => {
      if (!courseId || !expId) return;
      markPartComplete(courseId, expId, partId);
    },
    [courseId, expId, markPartComplete]
  );

  // Handle part tab change
  const handlePartChange = (partNum: number) => {
    setCurrentPart(partNum);
    // Also reset LabContext's currentStep to 1 so guided mode starts at step 1
    // of the new part. Without this, currentStep retains its value from the
    // previous part, making it impossible to interact with early steps.
    updateLabState({ currentStep: 1 });
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Handle back navigation
  const handleBack = () => {
    navigate(`/course/${courseId}`);
  };

  // Render loading state
  if (loading) {
    return (
      <div className="experiment-page loading">
        <div className="loading-content">
          <Loader2 size={48} className="spin" />
          <p>Loading experiment...</p>
        </div>
        <style>{pageStyles}</style>
      </div>
    );
  }

  // Render error state
  if (error || !config || !course) {
    return (
      <div className="experiment-page error">
        <div className="error-content">
          <AlertCircle size={48} />
          <h2>Error Loading Experiment</h2>
          <p>{error || 'Unknown error occurred'}</p>
          <button onClick={handleBack}>Back to Dashboard</button>
        </div>
        <style>{pageStyles}</style>
      </div>
    );
  }

  // Check if experiment has custom renderer
  const hasCustomRenderer = courseId && expId ? experimentRenderers[courseId]?.[expId] : undefined;

  // Render experiment content
  const renderExperimentContent = () => {
    if (!currentPart) return null;

    // If there's a custom renderer for this experiment and part, use it
    if (hasCustomRenderer && hasCustomRenderer[currentPart.name]) {
      const CustomRenderer = hasCustomRenderer[currentPart.name];
      return <CustomRenderer />;
    }

    // Default generic renderer using LabBench with functional step progression
    const currentStepData = currentPart.steps.find(s => s.id === (experimentState?.currentStep || 1));
    const allStepsComplete = currentPart.steps.every(s => isStepDone(s.id));

    const handleStepAction = (stepId: number) => {
      if (!isStepDone(stepId)) {
        handleCompleteStep(`${currentPart.name}-${stepId}`);
        // Move to next step
        const nextStep = currentPart.steps.find(s => s.id === stepId + 1);
        if (nextStep) {
          setCurrentStep(nextStep.id);
        }
      }
    };

    return (
      <div className="experiment-layout">
        {/* Steps Panel */}
        <div className="steps-panel">
          <div className="steps-header">
            <ListChecks size={16} />
            <h3>{currentPart.title}</h3>
          </div>
          <div className="steps-list">
            {currentPart.steps.map((step: ExperimentStep) => (
              <div
                key={step.id}
                className={`step-item ${
                  isStepActive(step.id) && !isStepDone(step.id) ? 'active' : ''
                } ${isStepDone(step.id) ? 'completed' : ''}`}
                onClick={() => !isStepDone(step.id) && setCurrentStep(step.id)}
              >
                <div
                  className={`step-number ${
                    isStepDone(step.id) ? 'done' : isStepActive(step.id) ? 'current' : ''
                  }`}
                >
                  {isStepDone(step.id) ? <Check size={12} /> : step.id}
                </div>
                <div className="step-content">
                  <div className="step-title-row">
                    <span
                      className={`step-title ${isStepActive(step.id) ? 'highlight' : ''}`}
                    >
                      {step.title}
                    </span>
                    {step.infoContent && (
                      <button
                        className="step-info-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowInfo(step.id);
                        }}
                      >
                        ?
                      </button>
                    )}
                  </div>
                  {isStepActive(step.id) && !isStepDone(step.id) && (
                    <span className="step-hint">{step.shortDescription}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lab Bench Area */}
        <LabBench
          experimentName={config.name}
          completedCount={getPartCompletedCount()}
          totalSteps={currentPart.steps.length}
          equipment={currentPart.equipment}
          labMode={appState.labMode}
          currentStep={experimentState?.currentStep || 1}
        >
          <div className="generic-experiment-ui">
            {/* Current Step Instruction */}
            {currentStepData && !allStepsComplete && (
              <div className="current-step-card">
                <div className="step-badge-large">Step {currentStepData.id}</div>
                <h3>{currentStepData.title}</h3>
                <p>{currentStepData.shortDescription}</p>
                {currentStepData.infoContent && (
                  <button
                    className="info-expand-btn"
                    onClick={() => setShowInfo(currentStepData.id)}
                  >
                    <HelpCircle size={16} />
                    Learn More
                  </button>
                )}
                <button
                  className="complete-step-btn"
                  onClick={() => handleStepAction(currentStepData.id)}
                >
                  <Check size={18} />
                  Complete Step
                </button>
              </div>
            )}

            {/* Part Complete Message */}
            {allStepsComplete && (
              <div className="part-complete-card">
                <div className="complete-icon">
                  <Check size={48} />
                </div>
                <h3>Part Complete!</h3>
                <p>You have completed all steps in {currentPart.title}.</p>
                {config.parts.length > experimentState?.currentPart! && (
                  <button
                    className="next-part-btn"
                    onClick={() => {
                      handlePartComplete(currentPart.name);
                      handlePartChange(experimentState!.currentPart + 1);
                    }}
                  >
                    Continue to Next Part
                  </button>
                )}
              </div>
            )}

            {/* Equipment Display */}
            <div className="equipment-display">
              <h4>Equipment</h4>
              <div className="equipment-grid">
                {currentPart.equipment.map((eq, idx) => (
                  <div key={idx} className="equipment-item">
                    <FlaskConical size={24} />
                    <span>{eq}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </LabBench>
      </div>
    );
  };

  // Calculate overall experiment progress
  const totalSteps = config.parts.reduce((sum, part) => sum + part.steps.length, 0);
  const completedSteps = experimentState?.completedSteps.length || 0;
  const progressPercent = Math.round((completedSteps / totalSteps) * 100);

  return (
    <div className="experiment-page">
      {/* Header */}
      <header className="page-header">
        <div className="header-left">
          <button className="back-btn" onClick={handleBack}>
            <ArrowLeft size={20} />
          </button>
          <div className="brand">
            <FlaskConical size={24} />
            <span>BDP vLab</span>
          </div>
          <span className="course-badge">{course.code}</span>
          <span className="exp-badge">Exp {config.number}</span>
        </div>
        <div className="header-center">
          <h2 className="exp-title">{config.name}</h2>
          <div className="progress-mini">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span>{progressPercent}%</span>
          </div>
        </div>
        <div className="header-right">
          <div className="mode-toggle">
            <button
              className={`mode-btn ${appState.labMode === 'guided' ? 'active' : ''}`}
              onClick={() => setLabMode('guided')}
            >
              Guided
            </button>
            <button
              className={`mode-btn ${appState.labMode === 'free' ? 'active' : ''}`}
              onClick={() => setLabMode('free')}
            >
              Free
            </button>
          </div>
          {config?.quiz && (
            <button className="quiz-launch-btn" onClick={() => setShowQuiz(true)}>
              <ClipboardList size={16} />
              Take Quiz
            </button>
          )}
          <span className="user-name">{appState.studentInfo?.name || 'Student'}</span>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* Part Tabs */}
      {config.parts.length > 1 && (
        <div className="part-tabs">
          {config.parts.map((part) => {
            const partStepsCompleted = experimentState?.completedSteps.filter((s) =>
              s.startsWith(`${part.name}-`)
            ).length || 0;
            const isPartComplete = part.steps.length > 0 && partStepsCompleted === part.steps.length;

            return (
              <button
                key={part.id}
                className={`part-tab ${
                  experimentState?.currentPart === part.id ? 'active' : ''
                } ${isPartComplete ? 'completed' : ''}`}
                onClick={() => handlePartChange(part.id)}
              >
                <span className="part-tab-number">
                  {part.steps.length === 0 ? (
                    <FlaskConical size={14} />
                  ) : isPartComplete ? (
                    <Check size={14} />
                  ) : (
                    part.id
                  )}
                </span>
                <span className="part-tab-title">{part.title}</span>
                {part.steps.length > 0 && (
                  <span className="part-tab-progress">
                    {partStepsCompleted}/{part.steps.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Main Content */}
      <main className="experiment-content">
        <ExperimentErrorBoundary>
          {renderExperimentContent()}
        </ExperimentErrorBoundary>
      </main>

      {/* Info Box Modal */}
      {showInfo && currentPart && (
        <InfoBox
          step={showInfo}
          experimentId={expId || ''}
          stepInfo={currentPart.steps.find(s => s.id === showInfo)?.infoContent}
          onClose={() => setShowInfo(null)}
        />
      )}

      {/* Quiz Panel Modal */}
      {showQuiz && config?.quiz && (
        <QuizPanel
          quiz={config.quiz}
          courseId={courseId || ''}
          experimentNumber={config.number}
          labData={experimentState?.experimentData}
          onClose={() => setShowQuiz(false)}
        />
      )}

      {/* AI Chat Panel */}
      <AIChatPanel
        experimentName={config?.name}
        partName={currentPart?.title}
        courseId={courseId}
        experimentId={expId}
      />

      <style>{pageStyles}</style>
    </div>
  );
}

// Page styles
const pageStyles = `
  .experiment-page {
    min-height: 100vh;
    background: var(--bg-dark, #1A1A1A);
    color: var(--text-primary, #FFFFFF);
    display: flex;
    flex-direction: column;
  }

  .experiment-page.loading,
  .experiment-page.error {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .loading-content,
  .error-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;
  }

  .loading-content .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .error-content {
    color: var(--danger, #EF4444);
  }

  .error-content button {
    background: var(--primary);
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: 600;
    margin-top: 8px;
  }

  /* Header - Fixed at top */
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 20px;
    background: var(--bg-surface, #2C2C2C);
    border-bottom: 1px solid var(--border, #4A4A4A);
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .back-btn {
    background: transparent;
    border: none;
    color: var(--text-secondary, #CCCCCC);
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    border-radius: 6px;
    transition: all 0.2s;
  }

  .back-btn:hover {
    color: var(--primary, #FF5A36);
    background: rgba(255, 90, 54, 0.1);
  }

  .brand {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 700;
    color: var(--primary, #FF5A36);
  }

  .course-badge,
  .exp-badge {
    background: var(--primary, #FF5A36);
    color: white;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
  }

  .exp-badge {
    background: var(--secondary, #00A86B);
  }

  .header-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }

  .exp-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
  }

  .progress-mini {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: var(--text-secondary);
  }

  .progress-mini .progress-bar {
    width: 100px;
    height: 4px;
    background: var(--bg-dark);
    border-radius: 2px;
    overflow: hidden;
  }

  .progress-mini .progress-fill {
    height: 100%;
    background: var(--secondary);
    transition: width 0.3s;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .mode-toggle {
    display: flex;
    background: var(--bg-dark, #1A1A1A);
    border-radius: 6px;
    padding: 2px;
  }

  .mode-btn {
    background: transparent;
    border: none;
    color: var(--text-muted, #999999);
    padding: 5px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s ease;
  }

  .mode-btn.active {
    background: var(--primary, #FF5A36);
    color: white;
  }

  .user-name {
    color: var(--text-secondary, #CCCCCC);
    font-size: 13px;
  }

  .logout-btn {
    background: transparent;
    border: 1px solid var(--border, #4A4A4A);
    color: var(--text-secondary, #CCCCCC);
    padding: 6px;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    transition: all 0.2s;
  }

  .logout-btn:hover {
    border-color: var(--danger, #EF4444);
    color: var(--danger, #EF4444);
  }

  /* Part Tabs */
  .part-tabs {
    display: flex;
    gap: 8px;
    padding: 12px 20px;
    background: var(--bg-surface);
    border-bottom: 1px solid var(--border);
  }

  .part-tab {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    background: var(--bg-elevated);
    border: 2px solid transparent;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .part-tab:hover {
    border-color: var(--border-light);
  }

  .part-tab.active {
    border-color: var(--primary);
    background: rgba(255, 90, 54, 0.1);
  }

  .part-tab.completed {
    border-color: var(--secondary);
  }

  .part-tab.completed .part-tab-number {
    background: var(--secondary);
    color: white;
  }

  .part-tab-number {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--bg-dark);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-secondary);
  }

  .part-tab.active .part-tab-number {
    background: var(--primary);
    color: white;
  }

  .part-tab-title {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
  }

  .part-tab-progress {
    font-size: 11px;
    color: var(--text-muted);
    background: var(--bg-dark);
    padding: 2px 8px;
    border-radius: 10px;
  }

  /* Main Content */
  .experiment-content {
    flex: 1;
    display: flex;
  }

  .experiment-layout {
    display: flex;
    gap: 16px;
    width: 100%;
    padding: 16px;
  }

  /* Steps Panel */
  .steps-panel {
    width: 260px;
    background: var(--bg-surface);
    border-radius: 12px;
    border: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex-shrink: 0;
  }

  .steps-header {
    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 10px;
    color: white;
  }

  .steps-header h3 {
    font-size: 14px;
    font-weight: 600;
    margin: 0;
  }

  .steps-list {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
  }

  .step-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    margin-bottom: 4px;
    transition: background 0.15s;
  }

  .step-item.active {
    background: rgba(255, 90, 54, 0.15);
  }

  .step-number {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
    background: var(--border);
    color: var(--text-muted);
  }

  .step-number.done {
    background: var(--secondary);
    color: white;
  }

  .step-number.current {
    background: var(--primary);
    color: white;
  }

  .step-content {
    flex: 1;
    min-width: 0;
  }

  .step-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .step-title {
    font-size: 12px;
    font-weight: 600;
  }

  .step-title.highlight {
    color: var(--primary-light);
  }

  .step-info-btn {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--accent);
    color: var(--bg-dark);
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: transform 0.15s;
    border: none;
    cursor: pointer;
  }

  .step-info-btn:hover {
    transform: scale(1.1);
  }

  .step-hint {
    font-size: 11px;
    color: var(--text-secondary);
    margin-top: 4px;
    display: block;
  }

  /* Generic Equipment Message */
  .generic-equipment-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: var(--text-muted);
    padding: 40px;
    gap: 16px;
  }

  .generic-equipment-message h4 {
    font-size: 18px;
    color: var(--text-secondary);
    margin: 0;
  }

  .generic-equipment-message p {
    max-width: 400px;
    line-height: 1.5;
  }

  .generic-equipment-message .equipment-list {
    background: var(--bg-surface);
    border-radius: 8px;
    padding: 16px 24px;
    margin-top: 8px;
    text-align: left;
  }

  .generic-equipment-message .equipment-list h5 {
    font-size: 13px;
    color: var(--text-secondary);
    margin: 0 0 12px 0;
  }

  .generic-equipment-message .equipment-list ul {
    margin: 0;
    padding-left: 20px;
    list-style-type: disc;
  }

  .generic-equipment-message .equipment-list li {
    font-size: 12px;
    margin-bottom: 4px;
    color: var(--text-primary);
  }

  /* Generic Experiment UI */
  .generic-experiment-ui {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    padding: 20px;
    width: 100%;
    max-width: 500px;
  }

  .current-step-card {
    background: var(--bg-surface);
    border: 2px solid var(--primary);
    border-radius: 16px;
    padding: 24px;
    text-align: center;
    width: 100%;
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .step-badge-large {
    display: inline-block;
    background: var(--primary);
    color: white;
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 12px;
  }

  .current-step-card h3 {
    font-size: 20px;
    margin: 0 0 8px 0;
    color: var(--text-primary);
  }

  .current-step-card p {
    color: var(--text-secondary);
    margin: 0 0 16px 0;
    font-size: 14px;
    line-height: 1.5;
  }

  .info-expand-btn {
    background: transparent;
    border: 1px solid var(--border);
    color: var(--text-secondary);
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 13px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    margin-bottom: 16px;
    transition: all 0.2s;
  }

  .info-expand-btn:hover {
    border-color: var(--primary);
    color: var(--primary);
  }

  .complete-step-btn {
    background: var(--secondary);
    color: white;
    border: none;
    padding: 14px 28px;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    transition: all 0.2s;
  }

  .complete-step-btn:hover {
    background: var(--secondary-dark);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 168, 107, 0.3);
  }

  .part-complete-card {
    background: var(--bg-surface);
    border: 2px solid var(--secondary);
    border-radius: 16px;
    padding: 32px;
    text-align: center;
    width: 100%;
    animation: fadeIn 0.3s ease;
  }

  .complete-icon {
    width: 80px;
    height: 80px;
    background: var(--secondary);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    margin: 0 auto 16px;
  }

  .part-complete-card h3 {
    font-size: 24px;
    margin: 0 0 8px 0;
    color: var(--secondary);
  }

  .part-complete-card p {
    color: var(--text-secondary);
    margin: 0 0 20px 0;
  }

  .next-part-btn {
    background: var(--primary);
    color: white;
    border: none;
    padding: 14px 28px;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .next-part-btn:hover {
    background: var(--primary-dark);
    transform: translateY(-2px);
  }

  .equipment-display {
    background: var(--bg-elevated);
    border-radius: 12px;
    padding: 16px;
    width: 100%;
  }

  .equipment-display h4 {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 12px 0;
  }

  .equipment-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .equipment-item {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--bg-surface);
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 12px;
    color: var(--text-secondary);
  }

  .equipment-item svg {
    color: var(--primary);
  }

  .step-item {
    cursor: pointer;
  }

  .step-item.completed {
    opacity: 0.7;
  }

  /* Quiz Launch Button */
  .quiz-launch-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    background: var(--accent, #FFEA00);
    color: var(--bg-dark, #1A1A1A);
    border: none;
    padding: 6px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .quiz-launch-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(255, 234, 0, 0.3);
  }
`;

export { experimentRenderers };
